import {
  APP_SMART_LINK_URL,
  APP_STORE_URL,
  INSTAGRAM_URL,
  PLAY_STORE_URL,
  TIKTOK_URL,
} from '../pages/home-redesign/constants';
import { OG_IMAGE_URL } from '../pages/home-redesign/cityLinks';
import { SITE_NAME, SITE_ORIGIN } from './constants';

export type JsonLdNode = Record<string, unknown>;

export type BreadcrumbItem = {
  name: string;
  url: string;
};

export type PlanJsonLdInput = {
  slug: string;
  name: string;
  date: string;
  location: string;
  citySlug: string;
  cityName: string;
  apuntadas: number;
  creatorName: string;
  imageUrl?: string;
};

export function buildJsonLdGraph(...nodes: JsonLdNode[]): JsonLdNode {
  return {
    '@context': 'https://schema.org',
    '@graph': nodes,
  };
}

export function organizationJsonLd(): JsonLdNode {
  return {
    '@type': 'Organization',
    '@id': `${SITE_ORIGIN}/#organization`,
    name: SITE_NAME,
    url: SITE_ORIGIN,
    logo: `${SITE_ORIGIN}/favicon.ico`,
    image: OG_IMAGE_URL,
    sameAs: [INSTAGRAM_URL, TIKTOK_URL],
  };
}

export function webSiteJsonLd(): JsonLdNode {
  return {
    '@type': 'WebSite',
    '@id': `${SITE_ORIGIN}/#website`,
    name: SITE_NAME,
    url: SITE_ORIGIN,
    publisher: { '@id': `${SITE_ORIGIN}/#organization` },
    inLanguage: ['es', 'en'],
  };
}

export function mobileAppJsonLd(): JsonLdNode {
  return {
    '@type': 'MobileApplication',
    '@id': `${SITE_ORIGIN}/#app`,
    name: SITE_NAME,
    operatingSystem: 'iOS, Android',
    applicationCategory: 'SocialNetworkingApplication',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'EUR',
    },
    installUrl: APP_SMART_LINK_URL,
    downloadUrl: [APP_STORE_URL, PLAY_STORE_URL],
    url: APP_SMART_LINK_URL,
  };
}

export function breadcrumbJsonLd(items: BreadcrumbItem[]): JsonLdNode {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export function planEventJsonLd(plan: PlanJsonLdInput): JsonLdNode {
  return {
    '@type': 'Event',
    '@id': `${SITE_ORIGIN}/${plan.slug}#event`,
    name: plan.name,
    startDate: plan.date,
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    eventStatus: 'https://schema.org/EventScheduled',
    location: {
      '@type': 'Place',
      name: plan.location,
      address: {
        '@type': 'PostalAddress',
        addressLocality: plan.cityName,
      },
    },
    organizer: {
      '@type': 'Person',
      name: plan.creatorName,
    },
    url: `${SITE_ORIGIN}/${plan.slug}`,
    maximumAttendeeCapacity: Math.max(plan.apuntadas, 1),
    ...(plan.imageUrl ? { image: plan.imageUrl } : {}),
  };
}

export function homePageJsonLd(): JsonLdNode {
  return buildJsonLdGraph(organizationJsonLd(), webSiteJsonLd(), mobileAppJsonLd());
}

export function appLinksPageJsonLd(): JsonLdNode {
  return buildJsonLdGraph(organizationJsonLd(), mobileAppJsonLd());
}

export function ciudadesPageJsonLd(): JsonLdNode {
  return buildJsonLdGraph(
    breadcrumbJsonLd([
      { name: SITE_NAME, url: SITE_ORIGIN },
      { name: 'Ciudades', url: `${SITE_ORIGIN}/ciudades` },
    ]),
  );
}

export function cityPlanesPageJsonLd(cityName: string, citySlug: string): JsonLdNode {
  return buildJsonLdGraph(
    breadcrumbJsonLd([
      { name: SITE_NAME, url: SITE_ORIGIN },
      { name: 'Ciudades', url: `${SITE_ORIGIN}/ciudades` },
      { name: cityName, url: `${SITE_ORIGIN}/ciudades/${citySlug}` },
    ]),
  );
}

export function planDetailPageJsonLd(plan: PlanJsonLdInput): JsonLdNode {
  return buildJsonLdGraph(
    planEventJsonLd(plan),
    breadcrumbJsonLd([
      { name: SITE_NAME, url: SITE_ORIGIN },
      { name: 'Ciudades', url: `${SITE_ORIGIN}/ciudades` },
      { name: plan.cityName, url: `${SITE_ORIGIN}/ciudades/${plan.citySlug}` },
      { name: plan.name, url: `${SITE_ORIGIN}/${plan.slug}` },
    ]),
  );
}
